/**
 * Student Service
 * Handles all student-related operations
 */
const StudentService = {
    /**
     * Get all students
     */
    getStudents() {
        return DataManager.getAll(DataManager.KEYS.STUDENTS) || [];
    },

    /**
     * Get student by ID
     */
    getStudent(id) {
        return DataManager.getById(DataManager.KEYS.STUDENTS, id);
    },

    /**
     * Cache only records confirmed by the server
     */
    cacheStudents(students, user) {
        const updates = new Map(students.map(student => [student.id, student]));
        const rows = this.getStudents().map(student => {
            const updated = updates.get(student.id);
            updates.delete(student.id);
            return updated || student;
        });
        rows.push(...updates.values());
        FileStorage.dataStore.students = rows;
        try { BrowserState.setItem(DataManager.KEYS.STUDENTS, JSON.stringify(rows)); } catch {}
        if (user && Array.isArray(AuthManager.users)) {
            const index = AuthManager.users.findIndex(item => item.id === user.id);
            if (index < 0) AuthManager.users.push(user); else AuthManager.users[index] = user;
        }
    },

    async persistStudent(method, suffix, data) {
        const epoch = AuthManager._epoch;
        try {
            const token = AuthManager.getCsrfToken();
            if (!token) throw new Error('Inicia sesión antes de guardar.');
            const response = await fetch('/api/students' + suffix, {
                method, headers: { 'X-SICAF-CSRF': token, 'Content-Type': 'application/json' },
                ...(data ? { body: JSON.stringify(data) } : {})
            });
            const result = await response.json().catch(() => null);
            if (epoch !== AuthManager._epoch) throw new Error('La sesión ha cambiado. Vuelve a entrar.');
            if (!response.ok || !result?.success) throw new Error(result?.error || 'No se pudo guardar el alumnado.');
            return result;
        } catch (error) {
            return { success: false, message: error.message || 'No se pudo conectar con el servidor.' };
        }
    },

    async createStudent(data) {
        const result = await this.persistStudent('POST', '', data);
        if (!result.success) return result;
        this.cacheStudents([result.student], result.user);
        return result.student;
    },

    async updateStudent(id, data) {
        const result = await this.persistStudent('PUT', '/' + encodeURIComponent(id), data);
        if (!result.success) return result;
        this.cacheStudents([result.student], result.user);
        return result.student;
    },

    // Excel import: the preview writes nothing; the import reads the same file again on the server.
    previewImport(file) {
        return this.persistStudent('POST', '/import/preview', { file });
    },

    async importStudents(file, formationId, academicYearStart) {
        const result = await this.persistStudent('POST', '/import', { file, formationId, academicYearStart });
        if (result.success) this.cacheStudents(result.students);
        return result;
    },

    // Access without any e-mail (admin).
    async giveAccess(ids) {
        const result = await this.persistStudent('POST', '/access', { ids });
        if (result.success && Array.isArray(AuthManager.users)) {
            for (const user of result.users) {
                const index = AuthManager.users.findIndex(item => item.id === user.id);
                if (index < 0) AuthManager.users.push(user); else AuthManager.users[index] = user;
            }
        }
        return result;
    },

    // New temporary password by e-mail, with the username and the sign-in link (admin).
    sendAccess(ids) {
        return this.persistStudent('POST', '/send-access', { ids });
    },

    async updateStudents(ids, changes) {
        const result = await this.persistStudent('POST', '/batch', { ids, changes });
        if (result.success) this.cacheStudents(result.students);
        return result;
    },

    async deleteStudent(id) {
        const result = await this.persistStudent('DELETE', '/' + encodeURIComponent(id), { confirmId: id });
        if (result.success) {
            const rows = this.getStudents().filter(student => student.id !== id);
            FileStorage.dataStore.students = rows;
            try { BrowserState.setItem(DataManager.KEYS.STUDENTS, JSON.stringify(rows)); } catch {}
        }
        return result;
    },

    /**
     * Warehouse access: the student is in a formation whose type gives access
     * (Plan de Estudios → Tipos → "Da acceso al almacén"). The server applies the same rule.
     */
    hasWarehouseAccess(studentId) {
        const student = this.getStudent(studentId);
        if (!student || !student.active) return false;
        return (student.formations || []).some(fId => {
            const formation = FormationService.getFormation(fId);
            return !!formation && typeof FormationTypeService !== 'undefined' && FormationTypeService.warehouseAccess(formation.type);
        });
    }
};

window.StudentService = StudentService;
